import { MaxTagSize } from '../../utils/commonConstants';
import TagUtils from '../../utils/tagUtils';
import { createStyles, makeStyles } from '@mui/styles';
import { RupInput, useStateRef } from 'copyright-clearance-center-common-ui-react';
import { observer } from 'mobx-react';
import React from 'react';

export interface IRenameTagListItemProps {
	name: string;
	helperText?: string;
	onCancel: () => void;
	onDone: (updatedName: string) => void;
	onValidation?: (candidateName: string) => string | null;
}

const useStyles = makeStyles(() =>
	createStyles({
		buttonsContainer: {
			display: 'flex',
			alignSelf: 'start',
			gap: '0.5rem',
			margin: '0.13rem 0 0 0.5rem'
		}
	})
);

export const RenameTagListItem: React.FunctionComponent<IRenameTagListItemProps> = ({
	name,
	helperText,
	onCancel,
	onDone,
	onValidation
}) => {
	const classes = useStyles();
	const inputHelpText = helperText ?? localize('Press the "Done" button to finalize your changes');

	const [inputValue, setInputValue, valueRef] = useStateRef(name);
	const [errorMessage, setErrorMessage] = React.useState(null);

	const rename = () => {
		const validationError = onValidation && onValidation(inputValue);
		if (!validationError) {
			onDone(inputValue);
		} else {
			setErrorMessage(validationError);
		}
	};

	const handleInput = (value: string | undefined) => {
		if (value === valueRef.current) {
			return;
		}

		setInputValue(value); // required to be able to display a new value after cleaning
		setErrorMessage(null);

		const cleanedValue = TagUtils.cleanup(value);
		setInputValue(TagUtils.clipLength(cleanedValue));
	};

	const handlePaste = (event: ClipboardEvent) => {
		event.preventDefault();
		const data = event.clipboardData.getData('text/plain');
		if (!data?.length) {
			return;
		}

		handleInput((event.target as HTMLInputElement).value + data);
	};

	return (
		<>
			<div>
				<RupInput
					testId="rename-tag-input"
					value={inputValue}
					maxLength={MaxTagSize}
					helperText={inputHelpText}
					error={errorMessage}
					css={{ width: '23rem' }}
					onInput={handleInput}
					onPaste={handlePaste}
				></RupInput>
			</div>
			<div className={classes.buttonsContainer}>
				<button
					type="button"
					className="rup-btn rup-btn rup-btn--text"
					data-test-id="rename-tag-cancel-btn"
					key="cancel"
					onClick={onCancel}
				>
					{localize('Cancel')}
				</button>
				<button
					type="button"
					className="rup-btn rup-btn"
					data-test-id="rename-tag-done-btn"
					key="done"
					onClick={rename}
					disabled={!!errorMessage}
				>
					{localize('Done')}
				</button>
			</div>
		</>
	);
};

export default observer(RenameTagListItem);
